/**
 * His vendor list's read and writes (XC-INV2PO DB6 note §3). The read is
 * `GET /api/v1/business-profile/ordering/vendors`; a save and an Undo are the Business Profile's own
 * two writes (`businessProfileWrites`): the whole list, `based_on` the answer the page opened with,
 * and the page's own save id. The tenant and the user are always the JWT's.
 */

import { businessProfileApi } from './businessProfileClient';
import {
  saveBusinessProfileFacts,
  undoBusinessProfileFact,
  type FactWriteResult,
  type VendorListFactWrite,
} from './businessProfileWrites';
import { listValue, VendorListSchema, type VendorList } from '@/types/businessProfile/vendorList';

export const VENDOR_LIST_PATH = '/api/v1/business-profile/ordering/vendors';

export class VendorListContractError extends Error {
  constructor() {
    super("Your vendor list's data couldn't be read.");
    this.name = 'VendorListContractError';
  }
}

export async function fetchVendorList(): Promise<VendorList> {
  const { data } = await businessProfileApi.get(VENDOR_LIST_PATH);
  const parsed = VendorListSchema.safeParse(data);
  if (!parsed.success) throw new VendorListContractError();
  return parsed.data;
}

export function vendorListBody(
  list: VendorList,
  changes: Record<string, string>,
  uiEventId: string,
): { facts: VendorListFactWrite[] } {
  return {
    facts: [
      {
        kind: 'business_policy',
        subtype: 'vendor_per_material',
        value: listValue(list, changes),
        ui_event_id: uiEventId,
        based_on: list.based_on,
      },
    ],
  };
}

export async function saveVendorList(
  list: VendorList,
  changes: Record<string, string>,
  uiEventId: string,
): Promise<FactWriteResult | undefined> {
  const filed = await saveBusinessProfileFacts(vendorListBody(list, changes, uiEventId));
  return filed[0];
}

/** The page's Undo of his latest change (`list.undo`), with this undo's own save id. */
export async function undoVendorList(list: VendorList, uiEventId: string): Promise<FactWriteResult> {
  if (!list.undo) throw new Error('There is no change of yours to undo.');
  return undoBusinessProfileFact(list.undo.ruling_ref, uiEventId, list.undo.undoes);
}
